const getComponents = (mode, palette) => {
  // Helper function to get theme-specific colors
  const getThemeColor = (lightColor, darkColor) => {
    switch (mode) {
      case 'light':
        return lightColor;
      case 'dark':
        return darkColor;
      default:
        return lightColor;
    }
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          transition: 'background-color 0.3s ease',
        },
        // Remove blue background from body
        '#root': {
          backgroundColor: 'transparent',
        },
        // Global theme-aware scrollbar styling
        '*': {
          // WebKit scrollbar styling (Chrome, Safari, Edge)
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: getThemeColor('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.1)'),
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: getThemeColor(palette.text.secondary, palette.primary.main), // Custom gray for light, primary for dark
            borderRadius: '4px',
            border: `2px solid ${palette.background.default}`, // Background default color
            '&:hover': {
              backgroundColor: getThemeColor(palette.text.disabled, palette.primary.light), // Darker gray for light, primary light for dark
            },
            '&:active': {
              backgroundColor: getThemeColor(palette.border.dark, palette.primary.dark), // Even darker gray for light, primary dark for dark
            },
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: palette.background.default,
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: `${getThemeColor(palette.text.secondary, palette.primary.main)} ${getThemeColor(
            'rgba(0, 0, 0, 0.05)',
            'rgba(255, 255, 255, 0.1)'
          )}`,
        },
        // Main page scrollbar (slightly larger)
        'html, body': {
          '&::-webkit-scrollbar': {
            width: '12px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: getThemeColor('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.1)'),
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: getThemeColor(palette.text.secondary, palette.primary.main), // Custom gray for light, primary for dark
            borderRadius: '6px',
            border: `2px solid ${palette.background.default}`,
            '&:hover': {
              backgroundColor: getThemeColor(palette.text.disabled, palette.primary.light), // Darker gray for light, primary light for dark
            },
            '&:active': {
              backgroundColor: getThemeColor(palette.border.dark, palette.primary.dark), // Even darker gray for light, primary dark for dark
            },
          },
        },
      },
    },
    // No MuiButtonBase.defaultProps here. `disableFocusRipple` is a Button /
    // IconButton / ToggleButton / Tab prop, not a ButtonBase one, so setting it
    // globally leaked the attribute onto every ButtonBase-derived DOM node
    // (MenuItem, ListItemButton, ...) and logged a React unknown-prop warning on
    // every page load. It also bought nothing: ButtonBase already defaults
    // `focusRipple` to false. The components that DO read the prop set it
    // themselves — see MuiButton, MuiIconButton and MuiToggleButton below.
    MuiButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          minHeight: 44, // WCAG compliance
          fontWeight: 700,
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderRadius: 20,
          },
          '&:focus-visible': {
            outline: '3px solid',
            outlineOffset: '2px',
            outlineColor: palette.primary.dark,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: palette.background.paper,
            '&:hover': {
              backgroundColor: palette.surface.secondary,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: palette.background.paper,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: palette.background.paper,
          boxShadow: getThemeColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.2)'),
          border: getThemeColor('1px solid surface.tertiary', '1px solid surface.secondary'),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          color: palette.text.primary,
          boxShadow: getThemeColor('0 1px 4px rgba(0, 0, 0, 0.08)', '0 2px 8px rgba(0, 0, 0, 0.2)'),
          borderBottom: getThemeColor('1px solid surface.tertiary', '1px solid surface.secondary'),
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.sidebar,
          borderRight: getThemeColor('1px solid surface.tertiary', '1px solid surface.secondary'),
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '64px !important',
          paddingLeft: '16px !important',
          paddingRight: '16px !important',
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&:hover': {
            backgroundColor: palette.action.hover,
          },
        },
      },
    },
    // Carries what the removed MuiButtonBase default used to cover here: the
    // billing toggle on /plans is the one ToggleButton in the app, and it reads
    // `disableFocusRipple` for real.
    MuiToggleButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: palette.background.paper,
          border: getThemeColor('1px solid surface.tertiary', '1px solid surface.secondary'),
          boxShadow: getThemeColor('0 8px 24px rgba(0, 0, 0, 0.12)', '0 8px 24px rgba(0, 0, 0, 0.3)'),
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: palette.action.hover,
          },
        },
      },
    },
  };
};

export default getComponents;
